import React from "react";
import { Gauge, X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { driverApi } from "../lib/api";

export default function DriverKmModal({ booking, mode = "pickup", onClose, onSaved }) {
  // mode: 'pickup' | 'delivery'
  const [pickupKm, setPickupKm] = React.useState(booking?.pickup_km ?? "");
  const [deliveryKm, setDeliveryKm] = React.useState(booking?.delivery_km ?? "");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    const payload = {};
    if (pickupKm !== "" && !Number.isNaN(parseFloat(pickupKm))) payload.pickup_km = parseFloat(pickupKm);
    if (deliveryKm !== "" && !Number.isNaN(parseFloat(deliveryKm))) payload.delivery_km = parseFloat(deliveryKm);
    if (!payload.pickup_km && !payload.delivery_km) {
      toast.error("Indtast mindst ét km-tal");
      return;
    }
    setBusy(true);
    try {
      const { data } = await driverApi().post(`/driver/bookings/${booking.id}/km`, payload);
      toast.success(`Km gemt${data.actual_km ? ` (faktisk: ${data.actual_km} km)` : ""}`);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme km");
    } finally { setBusy(false); }
  };

  const computed = (() => {
    const p = parseFloat(pickupKm);
    const d = parseFloat(deliveryKm);
    if (!Number.isFinite(p) || !Number.isFinite(d) || d < p) return null;
    return (d - p).toFixed(1);
  })();

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="km-modal">
      <div className="bg-white w-full sm:max-w-sm">
        <div className="border-b border-brand p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gauge size={18} className="text-brand-red" />
            <h2 className="font-display font-bold text-lg text-brand-dark">Registrér kilometer</h2>
          </div>
          <button onClick={onClose} className="text-brand-text2 hover:text-brand-dark" data-testid="km-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {booking?.booking_number && (
            <div className="text-xs text-brand-text2">
              Booking: <span className="font-mono font-bold text-brand-dark">{booking.booking_number}</span>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-1 block">
              Km ved afhentning
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={pickupKm}
              onChange={(e) => setPickupKm(e.target.value)}
              placeholder="fx 12345.5"
              className="w-full border border-brand p-3 text-lg font-mono focus:outline-none focus:border-brand-dark"
              data-testid="km-pickup"
              autoFocus={mode === "pickup"}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-1 block">
              Km ved levering
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={deliveryKm}
              onChange={(e) => setDeliveryKm(e.target.value)}
              placeholder="fx 12361.2"
              className="w-full border border-brand p-3 text-lg font-mono focus:outline-none focus:border-brand-dark"
              data-testid="km-delivery"
              autoFocus={mode === "delivery"}
            />
          </div>

          {computed && (
            <div className="bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900 flex items-center justify-between">
              <span>Faktisk kørt:</span>
              <span className="font-mono font-bold text-lg">{computed} km</span>
            </div>
          )}
        </div>

        <div className="border-t border-brand p-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 border border-brand text-brand-dark px-4 py-3 hover:bg-brand-bg text-sm font-bold"
          >
            Annullér
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 bg-brand-red text-white px-4 py-3 hover:bg-red-700 disabled:opacity-60 text-sm font-bold flex items-center justify-center gap-2"
            data-testid="km-save"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Gem km
          </button>
        </div>
      </div>
    </div>
  );
}
